import React, { useState, useRef, useEffect } from "react";
import { API_URLS } from "../../apiUrl";
import PropertyTypeDropdown from "../customHooks/propertyTypeDropdown";
import PropertyTypeDescriptionDropdown from "../customHooks/propertyTypeDescriptionDropdown";
import axiosInstance from "../../components/axiosInstance";

type FormData = {
  use_of_property: string;
  property_type_id: any;
  property_type_description_id: string;
};

const AddUseOfProperty: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    use_of_property: "",
    property_type_id: "",
    property_type_description_id: "",
  });

  const [errors, setErrors] = useState<any>({});
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [dataExistMessage, setDataExistMessage] = useState<string>("");
  const [dataFailedMessage, setDataFailedMessage] = useState<string>("");

  const formRef = useRef<HTMLFormElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const resetForm = () => {
    setFormData({
      use_of_property: "",
      property_type_id: "",
      property_type_description_id: "",
    });
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};
    
    if (!formData.use_of_property.trim()) {
      newErrors.use_of_property = "Use Of Property is required";
    } else if (!/^[A-Za-z0-9\s]+$/.test(formData.use_of_property)) {
      newErrors.use_of_property = "Use Of Property must contain only letters, numbers, and spaces";
    }

    if (!formData.property_type_id) {
      newErrors.property_type_id = "Property Type is required";
    }

    if (!formData.property_type_description_id) {
      newErrors.property_type_description_id = "Property Type Description is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        const response = await axiosInstance.post(`${API_URLS.addUseOfProperty}`, formData);

        if (response.data.data.message) {
          resetForm();
          setDataExistMessage(`${response.data.data.message}`);
          setTimeout(() => {
            setDataExistMessage("");
          }, 5000);
        } else {
          resetForm();
          setSuccessMessage(`${response.data.message}`);
          setTimeout(() => {
            setSuccessMessage("");
          }, 5000);
        }

        setErrors({});
      } catch (error) {
        setDataFailedMessage("Use Of Property Add failed.");
        setTimeout(() => {
          setDataFailedMessage("");
        }, 5000);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (formRef.current && !formRef.current.contains(e.target as Node)) {
        setErrors({});
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [handleDropdownToggleFromHook, isDropdownOpen, propertyTypes, loading, setIsDropdownOpen] = PropertyTypeDropdown();
  const [handleDropdownTogglePropertyDescription, isDropdownOpenPropertyDescription, , propertyDescriptions, loadingPropertyDescription, setIsDropdownOpenPropertyDescription] = PropertyTypeDescriptionDropdown(formData.property_type_id);

  const handlePropertyTypeSelect = (selectedValue: string) => {
    const selectedPropertyType = propertyTypes?.find((type: any) => type.type_name === selectedValue);
    if (selectedPropertyType) {
      setFormData((prevState) => ({
        ...prevState,
        property_type_id: selectedPropertyType.type_primary_id,
      }));
    }
    setIsDropdownOpen(false);
  };

  const handlePropertyTypeDescriptionSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;

    setFormData((prevFormData) => {
      const newSelections = prevFormData.property_type_description_id
        ? prevFormData.property_type_description_id.split(",")
        : [];

      if (checked) {
        newSelections.push(value);
      } else {
        const index = newSelections.indexOf(value);
        if (index > -1) {
          newSelections.splice(index, 1);
        }
      }

      return {
        ...prevFormData,
        property_type_description_id: newSelections.join(","),
      };
    });
  };

  const dropdownRefPropertyType = useRef<any>(null);
  const dropdownRefPropertyDescription = useRef<any>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRefPropertyType.current && !dropdownRefPropertyType.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }

      if (dropdownRefPropertyDescription.current && !dropdownRefPropertyDescription.current.contains(event.target as Node)) {
        setIsDropdownOpenPropertyDescription(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [setIsDropdownOpen, setIsDropdownOpenPropertyDescription]);

  const toggleDropdown = (
    e: React.MouseEvent<HTMLButtonElement>,
    setIsDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    e.preventDefault();
    setIsDropdownOpen((prev) => !prev);
  };

  return (
    <div className="max-w-full mx-auto">
      <div className="flex flex-col gap-5">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Add Use Of Property</h1>
        <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-gray-800">
          <form ref={formRef} onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label htmlFor="property_type" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Property Type <span className="text-red-600">*</span>
                </label>
                <div className="relative" ref={dropdownRefPropertyType}>
                  <button
                    onClick={(e) => toggleDropdown(e, setIsDropdownOpen)}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 pr-3 text-left flex items-center"
                    type="button"
                  >
                    {propertyTypes?.find((type: any) => type.type_primary_id === formData.property_type_id)?.type_name || "Select Property Type"}
                    <i className="ri-arrow-down-s-line ri-lg ml-auto"></i>
                  </button>

                  {isDropdownOpen && (
                    <select
                      id="property_type"
                      name="property_type"
                      value={propertyTypes?.find((type: any) => type.type_primary_id === formData.property_type_id)?.type_name || ""}
                      onChange={(e) => handlePropertyTypeSelect(e.target.value)}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 absolute top-full z-10"
                      size={10}
                    >
                      <option value="" disabled>Select Property Type</option>
                      {propertyTypes.map((property: any) => (
                        <option key={property.type_primary_id} value={property.type_name}>
                          {property.type_name}
                        </option>
                      ))}
                      {loading && <option>Loading...</option>}
                    </select>
                  )}
                </div>
                {errors.property_type_id && (
                  <p className="text-sm text-red-600 mt-1">{errors.property_type_id}</p>
                )}
              </div>

              <div>
                <label htmlFor="property_type_description" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Property Type Description <span className="text-red-600">*</span>
                </label>
                <div className="relative" ref={dropdownRefPropertyDescription}>
                  <button
                    onClick={(e) => toggleDropdown(e, setIsDropdownOpenPropertyDescription)}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 pr-3 text-left flex items-center"
                    type="button"
                  >
                    {formData.property_type_description_id.length > 0
                      ? propertyDescriptions
                        .filter((desc: any) => formData.property_type_description_id.split(",").includes(desc.id.toString()))
                        .map((desc: any) => desc.type_description_name)
                        .join(", ")
                      : "Select Property Type Description"}
                    <i className="ri-arrow-down-s-line ri-lg ml-auto"></i>
                  </button>

                  {isDropdownOpenPropertyDescription && (
                    <div className="absolute top-full w-full bg-gray-50 border border-gray-300 rounded-lg p-2.5 dark:bg-gray-700 dark:border-gray-600 max-h-[175px] overflow-y-auto z-10">
                      {propertyDescriptions.length === 0 ? (
                        <p className="text-sm text-red-600 dark:text-white">No Matching Property Type Description Found!</p>
                      ) : (
                        propertyDescriptions.map((property: any) => (
                          <div key={property.id} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`property_${property.id}`}
                              value={property.id}
                              checked={formData.property_type_description_id.split(",").includes(property.id.toString())}
                              onChange={handlePropertyTypeDescriptionSelect}
                              className="text-blue-600"
                            />
                            <label htmlFor={`property_${property.id}`} className="text-sm text-gray-900 dark:text-white">
                              {property.type_description_name}
                            </label>
                          </div>
                        ))
                      )}
                      {loadingPropertyDescription && <p>Loading...</p>}
                    </div>
                  )}
                </div>
                {errors.property_type_description_id && (
                  <p className="text-sm text-red-600 mt-1">{errors.property_type_description_id}</p>
                )}
              </div>

              <div>
                <label htmlFor="use_of_property" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Use Of Property <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="use_of_property"
                  name="use_of_property"
                  value={formData.use_of_property}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                />
                {errors.use_of_property && (
                  <p className="text-sm text-red-600 mt-1">{errors.use_of_property}</p>
                )}
              </div>

              <div className="flex items-end">
                <button type="submit" className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800">
                  Submit
                </button>
              </div>
            </div>
          </form>

          {successMessage && (
            <div className="mt-4 p-4 bg-green-600 text-white rounded-lg flex justify-between items-center">
              <span>{successMessage}</span>
              <button
                onClick={() => setSuccessMessage('')}
                className="ml-4 text-white font-bold text-lg focus:outline-none"
              >
                &times;
              </button>
            </div>
          )}

          {dataExistMessage && (
            <div className="mt-4 p-4 bg-yellow-500 text-white rounded-lg flex justify-between items-center">
              <span>{dataExistMessage}</span>
              <button
                onClick={() => setDataExistMessage('')}
                className="ml-4 text-white font-bold text-lg focus:outline-none"
              >
                &times;
              </button>
            </div>
          )}

          {dataFailedMessage && (
            <div className="mt-4 p-4 bg-red-600 text-white rounded-lg flex justify-between items-center">
              <span>{dataFailedMessage}</span>
              <button
                onClick={() => setDataFailedMessage('')}
                className="ml-4 text-white font-bold text-lg focus:outline-none"
              >
                &times;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddUseOfProperty;